/**
 * <atc-live-price> — keeps the main Add to cart button showing the live order total:
 *   "Add to cart — $170.99"  =  kit variant price × quantity  +  ticked kit add-ons
 *
 * Also writes the same total into the kit upsell's "Total" row ([data-kit-grand-total]).
 *
 * Listens to:
 *  - quantity input changes (#Quantity-{sectionId})
 *  - the theme's variantChange event (new variant price / sold out)
 *  - "kit-upsell:change" from kit-upsell.js (add-on total)
 *
 * The theme still controls the Sold out / Unavailable text: when the button is disabled, it is left alone.
 */
if (!customElements.get('atc-live-price')) {
  customElements.define(
    'atc-live-price',
    class AtcLivePrice extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.label = this.dataset.label || 'Add to cart';
        this.showPrice = this.dataset.showPrice === 'true';
        this.unitPrice = parseInt(this.dataset.unitPrice, 10) || 0;
        this.button = this.querySelector('[type="submit"][name="add"]');
        this.addonTotal = 0;

        // Add-on total from the kit upsell (it may have loaded before this element)
        const upsell = document.querySelector(`kit-upsell[data-section-id="${this.sectionId}"]`);
        if (upsell && typeof upsell.addonTotal === 'number') this.addonTotal = upsell.addonTotal;

        this.onUpsellChange = (e) => {
          if (!e.detail || e.detail.sectionId !== this.sectionId) return;
          this.addonTotal = e.detail.total || 0;
          this.render();
        };
        document.addEventListener('kit-upsell:change', this.onUpsellChange);

        this.onQtyChange = (e) => {
          if (e.target && e.target.id === `Quantity-${this.sectionId}`) this.render();
        };
        document.addEventListener('change', this.onQtyChange);
        document.addEventListener('input', this.onQtyChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const data = event && event.data;
            if (!data || data.sectionId !== this.sectionId) return;
            if (data.variant) this.unitPrice = data.variant.price;
            // the theme rewrites the button text right before publishing -> write ours after it
            setTimeout(() => this.render(), 0);
          });
        }

        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener('kit-upsell:change', this.onUpsellChange);
        document.removeEventListener('change', this.onQtyChange);
        document.removeEventListener('input', this.onQtyChange);
        if (this.unsubscribeVariant) this.unsubscribeVariant();
      }

      getQty() {
        const input = document.getElementById(`Quantity-${this.sectionId}`);
        const qty = input ? parseInt(input.value, 10) : 1;
        return qty > 0 ? qty : 1;
      }

      render() {
        const total = this.unitPrice * this.getQty() + this.addonTotal;
        const money = this.formatMoney(total);

        // Total row inside the kit upsell
        document
          .querySelectorAll(`kit-upsell[data-section-id="${this.sectionId}"] [data-kit-grand-total]`)
          .forEach((el) => (el.textContent = money));

        if (!this.showPrice || !this.button) return;
        if (this.button.hasAttribute('disabled')) return; // Sold out / Unavailable -> theme text stays
        if (this.button.getAttribute('aria-disabled') === 'true' && !this.button.classList.contains('loading')) return;

        const text = this.button.querySelector(':scope > span');
        if (text) text.textContent = `${this.label} — ${money}`;
      }

      formatMoney(cents) {
        const format = this.moneyFormat;
        const fmt = (value, decimals, thousands = ',', decimal = '.') => {
          const fixed = (value / 100).toFixed(decimals).split('.');
          const whole = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return fixed[1] ? `${whole}${decimal}${fixed[1]}` : whole;
        };
        const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
        if (!match) return fmt(cents, 2);
        let value;
        switch (match[1]) {
          case 'amount_no_decimals':
            value = fmt(cents, 0);
            break;
          case 'amount_with_comma_separator':
            value = fmt(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = fmt(cents, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            value = fmt(cents, 2, "'", '.');
            break;
          default:
            value = fmt(cents, 2);
        }
        return format.replace(match[0], value).replace(/<[^>]*>/g, '');
      }
    }
  );
}
